import {Injectable} from '@angular/core';
import {RestaurantRepository} from "../repositories/restaurant.repository";
import {Dtos} from "../utils/dtos";
import {FilterService} from "./filterer/filter.service";
import {Constants} from "../utils/constants";
import {DataManipulationService} from "./common/data-manipulation.service";
import {BaseCompany} from "./company.service";
import {Utilities} from "../utils/utilities";
import {Address} from "./order.service";
import {ValidatorService} from "./common/validator.service";
import {BaseService} from "./common/base.service";
import {MbColDefinition} from "../components/mb-datatable/models/mb-datatable.model";
import {MyStorageService} from "./common/my-storage.service";

@Injectable({
    providedIn: 'root'
})
export class RestaurantService extends BaseService<RestaurantRepository> {
    public REST_KEY = 'users_restaurants';

    constructor(repo: RestaurantRepository, filterService: FilterService, dataManipulationService: DataManipulationService, validatorService: ValidatorService, private myStorageService:MyStorageService) {
        super(repo, filterService, dataManipulationService, validatorService);
        this.typeName = 'Restaurant';
    }

    getOpenedRestaurants() {
        return this.repo.getOpenedRestaurants();
    }

    getAllForUser() {
        return this.repo.getAllForUser();
    }

    getAllForDashboardTable() {
        const filter = this.filterService.filter(Dtos.RESTAURANT.RestaurantForDashboardListDTO)
            .equal('deleted', false)
            .and()
            .notEqual("status", Constants.HISTORY_STATUS_DEPRECATED)
            .create();
        return this.repo.getAllByFilter(filter);
    }

    async storeUsersRestaurants() {
        let restaurants = await this.getAllForUser().toPromise();
        this.myStorageService.saveToCookies(this.REST_KEY, JSON.stringify(restaurants));
    }

    getUsersRestaurants() {
        return JSON.parse(this.myStorageService.getFromCookies(this.REST_KEY, '[]'));
    }

    addExtraDataForOne(restaurant: any): any {
        this.dataManipulationService.addNameToElement(restaurant);
        return restaurant
    }


    protected defineModelSpecificColumns(extraInfo?: any): any[] {
        return [
            MbColDefinition.new('historyId', 'base.historyId'),
            MbColDefinition.new('name', 'product.name'),
            MbColDefinition.new('modifier.email', 'general.modifier'),
        ];
    }

    deleteByHistoryId(historyId: number) {
        const path = "dashboard/admin/restaurant-histories/" + historyId;
        return this.repo.delete(path);
    }

    publish(id: number) {
        const path = "dashboard/admin/restaurants/" + id + "/publish";
        return this.repo.patch(path);
    }

    unpublish(id: any) {
        const path = "dashboard/admin/restaurants/" + id + "/unpublish";
        return this.repo.patch(path);
    }


    validate(model: any) {
        this.validateRestaurant(model, 10);
    }

    validateRestaurant(restaurant: BaseRestaurant, previouslySelectedIndex: number) {
        switch (previouslySelectedIndex) {
            case 2:
                this.validateStepThree(restaurant);
            case 1:
                this.validateStepTwo(restaurant);
            default:
                this.validateStepOne(restaurant);
        }
    }

    private validateStepOne(restaurant: BaseRestaurant) {
        if (restaurant.company == null || restaurant.company.id == null) {
            throw new Error("error.restaurant.notSelectedCompany");
        }
        Object.values<any>(restaurant.localizations).forEach(localization => {
            if (localization.name == null || localization.name == undefined || localization.name.length < 1) {
                throw new Error("error.restaurant.invalidName");
            }
        });
        if (restaurant.deliveryLimitAfterTomorrow < 1 || restaurant.deliveryLimitForTomorrow < 1 || restaurant.addressLimitAfterTomorrow < 1 || restaurant.addressLimitForTomorrow < 1) {
            throw new Error("error.restaurant.invalidLimits");
        }
        if (restaurant.openingDayDate == null) {
            throw new Error("error.restaurant.openingDayNotSet");
        }
        if(restaurant.status == "DRAFT" && restaurant.openingDayDate < new Date()){
            throw new Error("error.restaurant.invalidOpeningDayInDraftStatus");
        }
    }

    private validateStepTwo(restaurant: BaseRestaurant) {
        if (restaurant.maxCapacity < 1 || restaurant.licencedCapacity < 1) {
            throw new Error("error.restaurant.invalidCapacity");
        }
    }

    private validateStepThree(restaurant: BaseRestaurant) {
        this.validatorService.validateAddressList(restaurant.addresses, "restaurant");
        Object.keys(Constants.EMAIL_TYPES).forEach(key => {
            if (Constants.EMAIL_TYPES[key].required && restaurant.emails[key].length < 1) {
                throw new Error("error.restaurant.email.requiredMissing");
            }
            if (restaurant.emails[key].length > 0 && !Utilities.validateEmail(restaurant.emails[key])) {
                throw new Error("error.restaurant.email.invalidEmail");
            }
        });
        if (restaurant.phones.length < 1) {
            throw new Error("error.restaurant.phones.oneIsRequired");
        }
        if(restaurant.url.length<1){
            throw new Error("error.restaurant.urlIsRequired");
        }
    }

    saveOrUpdate(restaurant: BaseRestaurant) {
        restaurant.depositPercent = restaurant.depositPercent / 100;
        if (restaurant.id == null) {
            return this.save(restaurant);
        } else {
            return this.update(restaurant);
        }
    }
}

export class BasePlace {

    id: number = null;
    maxCapacity: number = 0;
    pufferCapacity: number = 0;
    cookingCapacity: number = 0;
    saturation: number = 0;
    available: boolean = true;
    name: string = "";
    type: string = "";
}

export class BaseRestaurantAddress {
    address: Address = null;

    constructor() {
        this.address = new Address();
    }
}

export class BasePhones {
    countryCode: string;
    number: number;
    isEdit: boolean = false;

    constructor(isEdit?: boolean) {
        this.countryCode = "+36";
        this.number = null;
        this.isEdit = isEdit != null;
    }
}

export class BaseRestaurant {
    id: number;
    company: BaseCompany;
    status:string = "DRAFT";
    localizations: any;
    private _openingDayDate: Date = null;
    addressLimitForTomorrow: number;
    addressLimitAfterTomorrow: number;
    deliveryLimitForTomorrow: number;
    deliveryLimitAfterTomorrow: number;
    depositPercent: number = 0;
    deliveryDeadline: string ='17:00:00';
    private _deliveryDeadlineDate:Date;
    deliveryDeadlineToleranceInMin = 30;
    licencedCapacity: number;
    maxCapacity: number;
    openingDay: string;
    places: BasePlace[];
    addresses: any;
    phones: BasePhones[];
    emails: any;
    url:string = "";
    defaultLanguage: string="";
    defaultCurrency: string="";
    serviceType: string = "RESTAURANT";

    constructor() {
        this.id = null;
        this.company = null;
        this.localizations = {};
        this._openingDayDate = new Date();
        this.openingDay = Utilities.dateToBackendString(this._openingDayDate);
        this.addressLimitForTomorrow = 0;
        this.addressLimitAfterTomorrow = 0;
        this.deliveryLimitForTomorrow = 0;
        this.deliveryLimitAfterTomorrow = 0;
        this.depositPercent = 0;
        this.licencedCapacity = 0;
        this.maxCapacity = 0;
        this.places = [];
        this.addresses = {BASE: new BaseRestaurantAddress()};
        this.phones = [];
        this.emails = {};
    }

    get deliveryDeadlineDate(): Date {
        return this._deliveryDeadlineDate;
    }

    set deliveryDeadlineDate(value: Date) {
        this._deliveryDeadlineDate = value;
        this.deliveryDeadline = Utilities.onlyTimeToBackendString(value);
    }

    get openingDayDate(): Date {
        return this._openingDayDate;
    }

    set openingDayDate(value: Date) {
        this._openingDayDate = value;
        this.openingDay = Utilities.dateToBackendString(value);
    }
}
